// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { MenuItem, Tooltip, Typography, styled } from '@mui/material';
import { ReactNode } from 'react';

const ActionItem = styled(MenuItem)(({ theme }) => ({
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: 'min-content 1fr',
  columnGap: theme.spacing(1),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': {
    borderBottom: 'none',
  },
}));

const DescriptionTypography = styled(Typography)({
  textWrap: 'wrap',
  gridColumn: '2 / 2',
});

interface Props {
  /**
   * The icon to display next to the label.
   */
  icon: ReactNode;

  /**
   * The primary action label.
   */
  label: string;

  /**
   * A description of the action.
   */
  description: string;

  /**
   * If true, the action is disabled.
   */
  isDisabled?: boolean;

  /**
   * A callback fired when the action is clicked.
   */
  onClick: () => void;

  /**
   * An optional tooltip for the menu item that only renders if provided
   */
  tooltip?: string;
}

/**
 * A menu item that displays an icon, label, and description.
 */
export function ActionMenuItem(props: Props) {
  const element = (
    <ActionItem disabled={props.isDisabled} onClick={props.onClick}>
      {props.icon}
      <Typography variant="subtitle2">{props.label}</Typography>
      <DescriptionTypography variant="body2" color="text.secondary">
        {props.description}
      </DescriptionTypography>
    </ActionItem>
  );
  return props.tooltip ? (
    <Tooltip arrow title={props.tooltip}>
      <span>{element}</span>
    </Tooltip>
  ) : (
    element
  );
}
